import { Fragment, useCallback, useMemo, useState } from 'react';

import { Button } from '@mui/material';
import useApi from 'shared/hooks/api';
import api from 'utils/api';
import { PageLoader, PageError, Modal } from 'components';
import { ConfirmModal } from 'shared/modals/confirmModal';

// components and parts
import Details from '../components/DetailsModal';
import New from './newModal';
import { DOLPHIN_API } from 'shared/constants/apis';
import { tableColumns } from './tableColumns';
import BasicTable from 'components/BasicTable';
import logger from 'utils/logger';
import { useSnackbar } from 'notistack';

const log = logger.createLogger('AdminOrganizations');

const IWGDashboardOrganizations = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [orgDetailsModalOpen, setOrgDetailsModalOpen] = useState(false);
  const [orgIdSelected, setOrgIdSelected] = useState(-1);
  const [orgDataSelected, setOrgDataSelected] = useState({});
  const [orgNewModalOpen, setOrgNewModalOpen] = useState(false);
  const [orgDeleteModalOpen, setOrgDeleteModalOpen] = useState(false);

  const getChangableOrgData = (org) => {
    return {
      name: org.name,
      accelerateEnabled: org.accelerateEnabled,
      organizationCode: org.organizationCode,
      paymentMethod: org.paymentMethod,
      contact: org.contact,
      cloudfrontEnabled: org.cloudfrontEnabled,
    };
  };

  const handleOpenDeleteOrg = useCallback((org) => {
    setOrgIdSelected(org.id);
    setOrgDataSelected(() => ({
      ...getChangableOrgData(org),
    }));
    setOrgDeleteModalOpen(true);
  }, []);

  const handleOpenOrgDetails = useCallback((org) => {
    setOrgIdSelected(org.id);
    setOrgDataSelected(() => ({
      ...getChangableOrgData(org),
    }));
    setOrgDetailsModalOpen(true);
  }, []);

  const columns = useMemo(
    () => tableColumns(handleOpenOrgDetails, handleOpenDeleteOrg),
    [handleOpenOrgDetails, handleOpenDeleteOrg]
  );

  const [{ data, error }, fetchOrgs] = useApi.useGet(
    DOLPHIN_API.ADMIN_ORGS,
    {}
  );
  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const handleUpdateOrg = async (orgId, updatedOrgJSON) => {
    const updatedOrgData = JSON.parse(updatedOrgJSON);
    await api.put(DOLPHIN_API.ADMIN_ORGS, {
      data: {
        ids: [orgId],
        updatedData: [updatedOrgData],
      },
    });
    await fetchOrgs();

    enqueueSnackbar('Organization updated successfully', {
      variant: 'success',
    });
  };

  const handleDeleteOrg = async (orgId) => {
    try {
      await api.delete(DOLPHIN_API.ADMIN_ORGS, {
        data: {
          ids: [orgId],
        },
      });

      await fetchOrgs();
      enqueueSnackbar('Organization deleted successfully', {
        variant: 'success',
      });
    } catch (error) {
      console.error(error);
      // Display an alert with the error message
      enqueueSnackbar(
        `Error deleting organization: ${
          error.message || 'Unknown error occurred'
        }`,
        {
          variant: 'error',
        }
      );
    } finally {
      setOrgDeleteModalOpen(false);
    }
  };

  data.sort((org1, org2) => {
    if (org1.id > org2.id) return 1;
    if (org1.id < org2.id) return -1;
    return 0;
  });

  const tableData = data.map((org) => {
    const owner = org.owner ? org.owner.username : 'null';
    return {
      id: org.id,
      orgName: org.name,
      orgCode: org.organizationCode,
      owner: owner,
      accelerateEnabled: org.accelerateEnabled,
      cloudfrontEnabled: org.cloudfrontEnabled,
      details: org,
      delete: org,
    };
  });

  return (
    <Fragment>
      <BasicTable
        columns={columns}
        data={tableData}
        handleRefreshClick={fetchOrgs}
        tableTitle="Organizations"
        rowsPerPageOptions={[10, 20, 50, 100]}
        initialPageSize={50}
        customToolbar={
          <Button
            color="primary"
            variant="contained"
            onClick={() => setOrgNewModalOpen(true)}>
            Create
          </Button>
        }
      />

      <Modal
        isOpen={orgNewModalOpen}
        width="700px"
        withCloseIcon={true}
        onClose={() => setOrgNewModalOpen(false)}
        renderContent={(modal) => (
          <New close={modal.close} fetchOrgs={fetchOrgs} />
        )}
      />

      <Modal
        isOpen={orgDetailsModalOpen}
        width="700px"
        withCloseIcon={true}
        onClose={() => setOrgDetailsModalOpen(false)}
        renderContent={() => (
          <Details
            dataType="Organization"
            dataId={orgIdSelected}
            data={orgDataSelected}
            updateData={handleUpdateOrg}
          />
        )}
      />

      <ConfirmModal
        isOpened={orgDeleteModalOpen}
        onCancel={() => setOrgDeleteModalOpen(false)}
        onConfirm={() => handleDeleteOrg(orgIdSelected)}
        title={`Do you want to DELETE Organization: ${orgDataSelected?.name}`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Fragment>
  );
};

export default IWGDashboardOrganizations;
